import { AfterViewInit, ChangeDetectorRef, Component, Input, OnInit, ViewChild } from '@angular/core';
import { ChannelSearchDialogComponent } from '@components/channel-search-dialog/channel-search-dialog.component';
import { ChannelService } from '@service/channel/channel.service';
import { Channel } from 'icreate/common/models/channel';
import { lastValueFrom, take } from 'rxjs';
import {
    ChannelType,
    getAllTypesSet,
    getAnalogTypesSet,
    getDigitalTypesSet,
    isDigital,
} from 'icreate/common/models/channel-type';
import { ElementSettings } from 'icreate/common/models/editor-viewer-element';
import * as _ from 'lodash';
import { ElementAttribute } from '@models/editor';
import { Vessel } from 'icreate/common/models/vessel';
import { CanvasInputMode } from '@service/canvas/canvas-input-mode.service';
import { AutosavePage } from '@service/auto-save-controller.service';
import { EditorService } from '@service/editor.service';
import { ElementIntegrationViewData } from 'icreate/common/models/element-integration';
import { ElementSettingsService } from '@service/canvas/element-settings.service';
import { SystemColor } from 'icreate/common/models/system-color';
import { SystemColorService } from '@service/system-color.service';
import { EditorMimicActionsService } from '@service/editor-mimic-actions.service';
import { MimicsService } from '@service/mimics.service';
import { MatDialog } from '@angular/material/dialog';
import { ButtonElementInteractionsComponent } from '@components/editor/button-element-interactions/button-element-interactions.component';
import { InputValueExtensionService } from '@service/canvas/inputValue-extension.service';
import { OutputValueExtensionService } from '@service/canvas/outputValue-extension.service';
import { BarExtensionService } from '@service/canvas/bar-extension.service';
import { ToggleInteractionsComponent } from '@components/editor/toggle-interactions/toggle-interactions.component';
import { BarElementSettingsComponent } from '@components/editor/bar-element-settings/bar-element-settings.component';

@Component({
    selector: 'app-control-element-settings',
    templateUrl: './control-element-settings.component.html',
    styleUrls: [
        './control-element-settings.component.css',
        '../default-element-settings/default-element-settings.component.css',
    ],
    standalone: false,
})
export class ControlElementSettingsComponent implements OnInit, AfterViewInit {
    @Input() selectedElement;
    @Input() mimicId: string;
    @Input() vessel: Vessel;
    @Input() userCanEdit: boolean;
    @Input() elementSettings: ElementSettings;
    @Input() elementIntegration: ElementIntegrationViewData;

    @ViewChild('buttonInteractions') buttonInteractions: ButtonElementInteractionsComponent;
    @ViewChild('buttonToggleInteractions') buttonToggleInteractions: ButtonElementInteractionsComponent;
    @ViewChild('toggleIntercations') toggleIntercations: ToggleInteractionsComponent;
    @ViewChild('barInteractions') barInteractions: BarElementSettingsComponent;

    assignedChannel: Channel;
    initialChannel: Channel;
    isLoadingChannel: boolean = false;
    setShouldDisableTypes: boolean = false;
    allowedTypesSet: Set<ChannelType>;
    initialSetting: ElementSettings;
    initialElement;

    systemColors: SystemColor[];

    constructor(
        private cdr: ChangeDetectorRef,
        private dialog: MatDialog,
        private channelService: ChannelService,
        private editorService: EditorService,
        private elementSettingsService: ElementSettingsService,
        private systemColorService: SystemColorService,
        private editorMimicActionsService: EditorMimicActionsService,
        private mimicService: MimicsService,
        private inputValueExtension: InputValueExtensionService,
        private outputValueExtension: OutputValueExtensionService,
        private barExtension: BarExtensionService
    ) {}

    async ngOnInit(): Promise<void> {
        if (!this.elementSettings) {
            this.elementSettings = {
                id: this.elementSettings?.id,
                channelTag: null,
                channelIsDigital: false,
                elementIntegrationId: this.elementIntegration?.id ?? null,
                elementId: this.selectedElementId,
                mimicId: this.mimicId,
                ranges: [],
            };
        }
        this.initialSetting = _.cloneDeep(this.elementSettings);
        this.isLoadingChannel = true;
        this.updateAllowedChannelTypes();
        this.systemColors = await this.systemColorService.getAll(this.vessel.id);
        const channelTag = this.elementSettings?.channelTag;
        if (channelTag) {
            const channel = await this.channelService.getByRowId(this.vessel.id, channelTag);
            if (channel) {
                this.assignedChannel = channel;
                this.initialChannel = structuredClone(this.assignedChannel);
            }
        }
        this.isLoadingChannel = false;
    }

    ngAfterViewInit(): void {
        this.initialElement = this.selectedElement.cloneNode(true);
    }

    async openChannelSelector() {
        if (!this.userCanEdit) return;
        const dialogRef = this.dialog.open(ChannelSearchDialogComponent, {
            data: {
                vesselId: this.vessel?.id,
                assignedChannel: this.assignedChannel,
                allowedTypesSet: this.allowedTypesSet,
                setShouldDisableTypes: this.setShouldDisableTypes,
                multipleSelection: false,
                preSelectedChannelsCount: 0,
                stopAutoSave: true,
                autoSavePage: AutosavePage.MIMIC,
            },
        });
        const channelSelected: Channel = await lastValueFrom(dialogRef.afterClosed());
        if (channelSelected) {
            this.assignedChannel = undefined;
            this.isLoadingChannel = true;
            const channel: Channel = await this.channelService.getByRowId(this.vessel.id, channelSelected.rowId);

            if (channel) {
                this.assignedChannel = channel;
                this.elementSettings = {
                    ...this.elementSettings,
                    id: this.elementSettings?.id,
                    channelTag: this.assignedChannel?.rowId ?? '',
                    channelIsDigital: isDigital(this.assignedChannel?.fcuData?.channelType),
                    elementIntegrationId: this.elementIntegration?.id ?? null,
                    elementId: this.selectedElementId,
                    mimicId: this.mimicId,
                    ranges: [],
                };
                const units = this.assignedChannel.opsData.measurementUnit;
                if (this.getElementType() === CanvasInputMode.INPUT_VALUE) {
                    this.inputValueExtension.udpateInputUnit(this.selectedElement, units);
                } else if (this.getElementType() === CanvasInputMode.OUTPUT_VALUE) {
                    this.outputValueExtension.udpateInputUnit(this.selectedElement, units);
                } else if (this.getElementType() === CanvasInputMode.BAR) {
                    this.barExtension.updateDisplay(
                        this.selectedElement,
                        this.assignedChannel,
                        null,
                        this.systemColors
                    );
                }
            }
            this.isLoadingChannel = false;
        }
    }

    removeAssignedChannel() {
        this.assignedChannel = undefined;
        this.elementSettings = {
            ...this.elementSettings,
            channelTag: '',
            channelIsDigital: false,
        };
        if (this.getElementType() === CanvasInputMode.INPUT_VALUE) {
            this.inputValueExtension.udpateInputUnit(this.selectedElement, 'Unit');
        } else if (this.getElementType() === CanvasInputMode.OUTPUT_VALUE) {
            this.outputValueExtension.udpateInputUnit(this.selectedElement, 'Unit');
        } else if (this.getElementType() === CanvasInputMode.BAR) {
            this.barExtension.updateDisplay(this.selectedElement, null, null, null);
        }
    }

    getElementType() {
        return this.selectedElement?.getAttribute(ElementAttribute.type);
    }

    hasChanges(): boolean {
        if (this.showButtonIntercations && this.buttonInteractions) {
            if (this.buttonInteractions.hasSettingError) return false;
            const currentStates = this.buttonInteractions.states;
            if (this.getElementType() == CanvasInputMode.BUTTON) {
                const currentAction = this.buttonInteractions.buttonAction;
                if (!_.isEqual(this.initialSetting.buttonAction, currentAction)) return true;
                if (!_.isEqual(this.initialSetting.flashingStatus, currentStates.flashingStatus)) return true;
            }
            if (!_.isEqual(this.initialSetting.disabledStatus, currentStates.disabledStatus)) return true;
        }
        if (this.showToggleInteractions) {
            if (this.toggleIntercations) {
                if (this.toggleIntercations.hasSettingError) return false;
                const currentState = this.toggleIntercations.currentToggleState;
                if (!_.isEqual(this.initialSetting.toggleState, currentState)) return true;
            }
            if (this.buttonToggleInteractions) {
                if (this.buttonToggleInteractions.hasSettingError) return false;
                const currentStates = this.buttonToggleInteractions.states;
                if (!_.isEqual(this.initialSetting.disabledStatus, currentStates.disabledStatus)) return true;
            }
        }
        if (this.showBarInteractions) {
            if (this.barInteractions.hasChanges) return true;
        }
        return !_.isEqual(this.initialSetting, this.elementSettings);
    }

    async save(): Promise<boolean> {
        this.editorService.showLoadingSpinner(true);
        if (this.showButtonIntercations) this.buttonInteractions?.updateSettings();
        if (this.showToggleInteractions) {
            this.buttonToggleInteractions?.updateSettings();
            this.toggleIntercations?.updateSettings();
        }
        try {
            if (!this.elementSettings?.id) {
                const { newId } = await this.elementSettingsService.create(this.elementSettings);
                this.elementSettings.id = newId;
            } else {
                await this.elementSettingsService.update(this.elementSettings);
            }

            if (
                this.showBarInteractions &&
                (!_.isEqual(this.initialChannel, this.assignedChannel) || this.barInteractions.hasChanges)
            ) {
                const currentSvgContent = this.editorMimicActionsService.serializeCurrentSvg();
                const currentMimic = this.editorService.getCurrentMimic();
                currentMimic.svgcontent = currentSvgContent;
                await this.mimicService.update(currentMimic, true);
                this.editorService.updateMimicContent(currentSvgContent);
                const currentElementId = this.selectedElement.getAttribute(ElementAttribute.id);
                this.selectedElement = document.querySelector(`#${currentElementId}`);
                this.barInteractions.updateInitialElement(this.selectedElement);
                this.initialElement = this.selectedElement.cloneNode(true);
            }
            this.initialSetting = _.cloneDeep(this.elementSettings);
            this.initialChannel = structuredClone(this.assignedChannel);
            return true;
        } catch (err) {
            console.error(err);
            throw Error();
        } finally {
            this.editorService.showLoadingSpinner(false);
        }
    }

    discardSettings() {
        if (this.getElementType() === CanvasInputMode.BAR) {
            if (this.initialSetting.channelTag != this.elementSettings.channelTag) {
                if (!this.initialSetting.channelTag) {
                    this.barExtension.updateDisplay(this.selectedElement, null, null, this.systemColors);
                } else {
                    this.barExtension.updateDisplay(this.selectedElement, this.initialChannel, null, this.systemColors);
                }
            }
            this.elementSettings = _.cloneDeep(this.initialSetting);
            this.assignedChannel = _.cloneDeep(this.initialChannel);
            const svgcontent = document.getElementById('svgcontent');
            const layerContent = svgcontent.querySelector('.layer');
            layerContent.replaceChild(this.initialElement, this.selectedElement);
            this.selectedElement = this.initialElement.cloneNode(true);
        }
    }

    get selectedElementId() {
        return this.selectedElement ? this.selectedElement.getAttribute(ElementAttribute.id) : '';
    }

    get isGaugeElement() {
        if (!this.isComponent) return false;
        return this.selectedElement.getAttribute(ElementAttribute.iCreateType) == CanvasInputMode.GAUGE;
    }

    get isComponent() {
        if (!this.selectedElement) return false;
        return this.selectedElement.getAttribute(ElementAttribute.iCreateType);
    }

    get hasValidChannelState(): boolean {
        return !this.assignedChannel || this.isLoadingChannel;
    }

    get showButtonIntercations(): boolean {
        if (this.hasValidChannelState) return false;
        const elementType = this.getElementType();
        return elementType === CanvasInputMode.BUTTON;
    }

    get showToggleInteractions() {
        if (this.hasValidChannelState) return false;
        const elementType = this.getElementType();
        return elementType === CanvasInputMode.TOGGLE;
    }

    get showStatusInteractions() {
        if (this.hasValidChannelState) return false;
        const elementType = this.getElementType();
        return elementType === CanvasInputMode.STATUS_INDICATOR;
    }

    get showBarInteractions() {
        if (this.hasValidChannelState) return false;
        const elementType = this.getElementType();
        return elementType === CanvasInputMode.BAR;
    }

    private updateAllowedChannelTypes() {
        const elementType = this.getElementType();
        if (elementType === CanvasInputMode.INPUT_VALUE) {
            this.allowedTypesSet = getAllTypesSet();
            this.setShouldDisableTypes = false;
        } else if (elementType === CanvasInputMode.OUTPUT_VALUE) {
            this.allowedTypesSet = getAllTypesSet();
            this.setShouldDisableTypes = false;
        } else if (elementType === CanvasInputMode.BUTTON) {
            this.allowedTypesSet = getAllTypesSet();
            this.setShouldDisableTypes = false;
        } else if (elementType === CanvasInputMode.STATUS_INDICATOR) {
            this.allowedTypesSet = getAllTypesSet();
            this.setShouldDisableTypes = false;
        } else if (elementType === CanvasInputMode.TOGGLE) {
            this.allowedTypesSet = getDigitalTypesSet();
            this.setShouldDisableTypes = true;
        } else if (elementType === CanvasInputMode.BAR) {
            this.allowedTypesSet = getAnalogTypesSet();
            this.setShouldDisableTypes = true;
        }
    }
}
